import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Mixpanel from '../utils/mixpanel';
import logger from '../utils/logger';
import GuideModal from './GuideModal';
import './DesktopComponentStyles.css';


const RelaxationTimer = () => {
  const navigate = useNavigate();
  const [screen, setScreen] = useState('selection'); // 'selection', 'timer', 'completion'
  const [duration, setDuration] = useState(1); // default to 1 minute
  const [remainingTime, setRemainingTime] = useState(0); // in seconds
  const [isRunning, setIsRunning] = useState(false);
  const [actualDuration, setActualDuration] = useState(0); // actual time spent in seconds
  
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const audioRef = useRef(null);

  // Special durations with dedicated audio tracks
  const specialDurations = [1, 2, 4, 15];

  // Initialize and manage audio
  useEffect(() => {
    // Clean up previous audio if exists
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Set up the appropriate audio file
    let audioUrl;
    if (duration === 15) {
      // Special case for 15min PowerNap
      audioUrl = `/audio/relaxation/15min-PowerNap.mp3`;
    } else {
      // Regular relaxation audio files
      audioUrl = `/audio/relaxation/${duration}min-relaxation.mp3`;
    }
    
    logger.debug(`Using audio for ${duration} minutes:`, audioUrl);

    audioRef.current = new Audio(audioUrl);
    
    // Preload the audio
    audioRef.current.load();
    
    // Add event listeners for debugging
    audioRef.current.addEventListener('canplaythrough', () => {
      logger.debug('Audio loaded and ready to play');
    });
    
    audioRef.current.addEventListener('error', (e) => {
      logger.error('Error loading audio:', e);
    });
    
    audioRef.current.addEventListener('play', () => {
      logger.debug('Audio started playing');
    });
    
    // Handle completion when audio ends
    audioRef.current.addEventListener('ended', () => {
      logger.debug('Audio ended');
      // If the timer is still running, complete the session
      if (isRunning && screen === 'timer') {
        completeSession();
      }
    });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [duration, isRunning, screen]);

  // Set up the timer when starting
  useEffect(() => {
    if (screen === 'timer' && isRunning) {
      startRelaxationSession();
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [screen, isRunning]);

  // Clean up on component unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const startRelaxationSession = () => {
    // Set the initial remaining time
    if (remainingTime === 0) {
      setRemainingTime(duration * 60);
    }
    
    // Record the start time
    startTimeRef.current = Date.now() - ((duration * 60) - remainingTime) * 1000;
    
    // Try to play audio
    if (audioRef.current) {
      // Create a one-time click handler to play audio
      const handleUserInteraction = () => {
        audioRef.current.play().catch(error => {
          logger.error('Error playing audio:', error);
        });
        document.removeEventListener('click', handleUserInteraction);
      };
      
      // Add the event listener
      document.addEventListener('click', handleUserInteraction);
      
      // Try to play immediately (may be blocked by browser)
      audioRef.current.play().catch(error => {
        logger.debug('Waiting for user interaction to play audio:', error);
      });
    }
    
    // Start the timer
    timerRef.current = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          // Timer complete
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
      
      // Update actual duration
      const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setActualDuration(elapsedSeconds);
    }, 1000);
    
    // Track relaxation start with Mixpanel
    Mixpanel.track('Relaxation Started', {
      duration_minutes: duration,
      timestamp: new Date().toISOString()
    });
  };

  const completeSession = () => {
    // Stop the timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // Stop the audio
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    // Calculate final duration
    const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
    setActualDuration(elapsedSeconds);
    
    // Save the session
    saveSession(elapsedSeconds);
    
    // Mark first relaxation session task as completed
    markRelaxationTaskCompleted();
    
    // Update UI
    setIsRunning(false);
    setScreen('completion');
    
    // Track completion with Mixpanel
    Mixpanel.track('Completed Relaxation Session', {
      duration_minutes: duration,
      actual_duration_seconds: elapsedSeconds
    });
  };

  const markRelaxationTaskCompleted = () => {
    // Set localStorage flag
    localStorage.setItem('firstRelaxationSession', 'true');
    
    // Update the server if possible
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Remove the '/api' prefix since the api module already includes it in its baseURL
        api.post('/users/starter-task', {
          task: 'firstRelaxationSession',
          completed: true
        })
        .catch(error => {
          logger.error('Error updating relaxation completion status on server:', error);
        });
      } catch (error) {
        logger.error('Error updating relaxation completion status:', error);
      }
    }
  };

  const saveSession = async (seconds) => {
    try {
      await api.post('/relaxation/sessions', {
        durationMinutes: Math.round(duration),
        actualDurationSeconds: seconds,
        notes: ''
      });
      
      logger.debug('Relaxation session saved successfully');
    } catch (error) {
      logger.error('Error saving relaxation session:', error);
    }
  };

  const pauseResume = () => {
    if (isRunning) {
      // Pause the timer
      clearInterval(timerRef.current);
      
      // Calculate and update the actual duration
      const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setActualDuration(elapsedSeconds);
      
      // Pause audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      // Track relaxation paused with Mixpanel
      Mixpanel.track('Relaxation Paused', {
        remaining_seconds: remainingTime,
        elapsed_seconds: elapsedSeconds,
        timestamp: new Date().toISOString()
      });
      
      // Update state
      setIsRunning(false);
    } else {
      // Resume the timer
      startTimeRef.current = Date.now() - (actualDuration * 1000);
      
      // Resume audio
      if (audioRef.current) {
        audioRef.current.play().catch(error => {
          logger.error('Error resuming audio:', error);
        });
      }
      
      // Track relaxation resumed with Mixpanel
      Mixpanel.track('Relaxation Resumed', {
        remaining_seconds: remainingTime,
        elapsed_seconds: actualDuration,
        timestamp: new Date().toISOString()
      });
      
      // Start the timer again
      startRelaxationSession();
      
      // Update state
      setIsRunning(true);
    }
  };

  const endSession = () => {
    // Confirm the user wants to end the session
    if (window.confirm('Are you sure you want to end your relaxation session?')) {
      // Clear the timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Calculate final duration
      const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setActualDuration(elapsedSeconds);
      
      // Stop audio
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      // Track relaxation ended early with Mixpanel
      Mixpanel.track('Relaxation Ended Early', {
        planned_duration_minutes: duration,
        actual_duration_seconds: elapsedSeconds,
        remaining_seconds: remainingTime,
        timestamp: new Date().toISOString()
      });
      
      // Save session to backend
      saveSession(elapsedSeconds);
      
      // Show completion screen
      setScreen('completion');
      setIsRunning(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const startSession = () => {
    setScreen('timer');
    setIsRunning(true);
  };

  const startNewSession = () => {
    setScreen('selection');
  };

  // Selection Screen
  if (screen === 'selection') {
    return (
      <>
        <GuideModal guideKey="Relaxation" />
        <div className="desktop-standard-container max-w-md mx-auto p-4">
          <h1 className="text-2xl font-bold text-center mb-6">Relaxation Timer</h1>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Relaxation Duration
            </label>
            <div className="grid grid-cols-3 gap-4 mb-8">
              {specialDurations.map(mins => (
                <button
                  key={mins}
                  className={`py-4 rounded-lg transition ${
                    duration === mins 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200 border border-indigo-300'
                  }`}
                  onClick={() => setDuration(mins)}
                >
                  <span className="block text-lg font-medium">{mins} min</span>
                  <span className="block text-xs mt-1">
                    {mins === 1 ? "Quick" : mins === 2 ? "Short" : mins === 15 ? "PowerNap" : "Standard"}
                  </span>
                </button>
              ))}
            </div>
          </div>
          
          <button 
            className="w-full py-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
            onClick={startSession}
          >
            Start Relaxation
          </button>
        </div>
      </>
    );
  }
  
  // Timer Screen
  if (screen === 'timer') {
    return (
      <>
        <div className="desktop-standard-container max-w-md mx-auto p-4 text-center">
          <div className="mb-10">
            <h2 className="text-sm font-medium text-gray-500 mb-2">
              Relaxation in Progress
            </h2>
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 mb-4">
              <h1 className="text-6xl font-bold mb-2">
                {formatTime(remainingTime)}
              </h1>
              <p className="text-gray-500">
                Remaining
              </p>
            </div>
          </div>
          
          <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
            <button 
              className="flex-1 py-4 bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300 transition focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
              onClick={endSession}
            >
              End Relaxation
            </button>
            <button 
              className="flex-1 py-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
              onClick={pauseResume}
            >
              {isRunning ? 'Pause' : 'Resume'}
            </button>
          </div>
          
          {/* Relaxation Tips */}
          <div className="mt-10 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Relaxation Tips</h3>
            <p className="text-sm text-gray-600">
              Find a comfortable position. Breathe naturally and focus on your breath.
              If your mind wanders, gently bring your attention back to your breath.
            </p>
          </div>
        </div>
      </>
    );
  }

  // Completion Screen
  if (screen === 'completion') {
    const minutes = Math.floor(actualDuration / 60);
    const seconds = actualDuration % 60;
    const formattedDuration = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    
    return (
      <>
        <div className="desktop-standard-container max-w-md mx-auto p-4 text-center">
          <div className="bg-indigo-50 rounded-xl p-8 mb-8">
            <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2">Relaxation Complete</h1>
            <p className="text-gray-600">
              Great job taking time for your mental wellbeing
            </p>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-8 mb-8">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">Total Relaxation Time</p>
              <p className="text-4xl font-bold mb-2">{formattedDuration}</p>
              <p className="text-sm text-gray-500">
                {actualDuration > 0 
                  ? `That's ${Math.floor(actualDuration / 60)} ${Math.floor(actualDuration / 60) === 1 ? 'minute' : 'minutes'} and ${actualDuration % 60} ${actualDuration % 60 === 1 ? 'second' : 'seconds'} of mindfulness`
                  : 'Every moment counts'}
              </p>
            </div>
          </div>
          
          <button 
            className="w-full py-4 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
            onClick={startNewSession}
          >
            New Relaxation
          </button>
          <button 
            className="w-full py-4 bg-gray-200 text-gray-800 font-medium rounded-lg hover:bg-gray-300 transition focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50"
            onClick={() => navigate('/dashboard')}
          >
            Return to Dashboard
          </button>
        </div>
      </>
    );
  }
};

export default RelaxationTimer;
